import React from "react";
import { Bom, ModuleItem, ReportAssignmentRow, ReportTypeDefinition } from "./types";
import { bomStats, dueLabel, rowState } from "./logic";
import { AssignModuleRow } from "./AssignModuleRow";
import { RaIcon } from "./RaIcon";

interface AssignModulesSectionProps {
  reportTypes: ReportTypeDefinition[];
  boms: Bom[];
  rows: ReportAssignmentRow[];
  modules: ModuleItem[];
  activeBomId: number;
  flashRowId: number | null;
  /** True while an API action is running; the actions are disabled meanwhile. */
  busy: boolean;
  historyCountFor: (reportId: number) => number;
  totalHistoryCount: number;
  onSelectBom: (bomId: number) => void;
  onChooseModules: (row: ReportAssignmentRow, anchor: HTMLElement) => void;
  onConfirm: (rowId: number) => void;
  onConfirmAllReady: (bomId: number, readyRowIds: number[]) => void;
  onOpenHistory: (reportId: number | null) => void;
}

/**
 * Section 2 — Assign modules to reports: per-BOM tabs, one row per report
 * of the active BOM, and a footer to bulk-confirm rows that already have
 * modules picked.
 */
export const AssignModulesSection: React.FC<AssignModulesSectionProps> = ({
  reportTypes,
  boms,
  rows,
  modules,
  activeBomId,
  flashRowId,
  busy,
  historyCountFor,
  totalHistoryCount,
  onSelectBom,
  onChooseModules,
  onConfirm,
  onConfirmAllReady,
  onOpenHistory,
}) => {
  const activeBom = boms.find((b) => b.id === activeBomId) ?? boms[0];
  const rowsForActiveBom = rows.filter((r) => r.bomId === activeBomId);
  const typeByKey = new Map(reportTypes.map((t) => [t.key, t]));

  const usedModuleIds = new Set<number>();
  rowsForActiveBom.forEach((r) => r.moduleIds.forEach((id) => usedModuleIds.add(id)));
  const unusedModules = modules.filter((m) => m.bomId === activeBomId && !usedModuleIds.has(m.id));

  const readyToConfirmIds = rowsForActiveBom
    .filter((r) => rowState(r) === "to_confirm")
    .map((r) => r.id);

  return (
    <section className="sec sec-assign">
      <div className="sh">
        <span className="num">2</span>
        <div>
          <h2>Assign modules to reports</h2>
          <div className="sub">
            Which modules feed each report. Each one starts from its rule. Pick the modules, then
            confirm.
          </div>
        </div>
        <span className="sp" />
        <button type="button" className="btn sm" onClick={() => onOpenHistory(null)}>
          <RaIcon name="hist" />
          Change history
          {totalHistoryCount > 0 && (
            <>
              {" "}
              <span className="cnt">{totalHistoryCount}</span>
            </>
          )}
        </button>
      </div>

      <div className="stk">
        <div className="bomtabs" role="tablist">
          {boms.map((b) => {
            const stats = bomStats(rows.filter((r) => r.bomId === b.id));
            const pct = stats.total ? Math.round((stats.confirmed / stats.total) * 100) : 0;
            return (
              <button
                key={b.id}
                type="button"
                role="tab"
                aria-selected={b.id === activeBomId}
                className={`bt${b.id === activeBomId ? " on" : ""}`}
                onClick={() => onSelectBom(b.id)}
              >
                <b>
                  {b.name} <em className="bd">{b.moduleCount} modules</em>
                </b>
                <span>
                  {stats.confirmed} of {stats.total} confirmed
                  {stats.needsModules > 0 && (
                    <>
                      {" · "}
                      <em className="flag">
                        <RaIcon name="alert" />
                        {stats.needsModules} needs modules
                      </em>
                    </>
                  )}
                </span>
                <span className="pb">
                  <i style={{ width: `${pct}%` }} />
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="table-wrapper" style={{ overflowX: "auto"}}>
        <div className="rh" style={{ textAlign: "left"}}>
          <div>Report</div>
          <div>Modules used</div>
          <div>Status</div>
          <div />
        </div>

        <div className="rows">
          {rowsForActiveBom.length === 0 && (
            <div className="hempty">No reports are planned for this BOM.</div>
          )}
          {rowsForActiveBom.map((row) => {
            const type = typeByKey.get(row.typeKey);
            if (!type) return null;
            return (
              <AssignModuleRow
                key={row.id}
                row={row}
                type={type}
                dueText={dueLabel(row.dueDate)}
                modules={modules}
                historyCount={historyCountFor(row.id)}
                flash={flashRowId === row.id}
                disabled={busy}
                onChooseModules={(anchor) => onChooseModules(row, anchor)}
                onConfirm={() => onConfirm(row.id)}
                onOpenHistory={() => onOpenHistory(row.id)}
              />
            );
          })}
        </div>
      </div>

      <div className="bomfoot">
        <div className="bf-l">
          <button
            type="button"
            className="btn sm"
            disabled={busy || readyToConfirmIds.length === 0}
            onClick={() => onConfirmAllReady(activeBomId, readyToConfirmIds)}
          >
            <RaIcon name="check" />
            Confirm all ready{activeBom ? ` in ${activeBom.name}` : ""}
          </button>
          <span className="bf-n">
            {readyToConfirmIds.length
              ? `${readyToConfirmIds.length} ${
                  readyToConfirmIds.length === 1 ? "report" : "reports"
                } ready to confirm`
              : "Nothing left to confirm"}
          </span>
        </div>
        <span className="bf-r">
          <RaIcon name="lock" />
          Confirmed reports are locked. Changes after that need a reason and are saved in the
          history.
        </span>
      </div>

      {unusedModules.length > 0 && (
        <div className="spare">
          <div className="sp-h">
            <b>Not used in any report ({unusedModules.length})</b>
            <span>
              Spares have no test steps. Modules that did not pass inspection stay out of the
              reports that use every module.
            </span>
          </div>
          <div className="chips">
            {unusedModules.map((m) => (
              <React.Fragment key={m.id}>
                <span className="mc">{m.serial}</span>
                <span className="why">
                  {m.spare ? "Spare" : !m.passedInspection ? "Not pass" : "Not assigned"}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};